import {
  type AgentId,
  type AgentModeWritableKey,
  agentQueueScope,
  type LlmRoutePurposeAny,
} from '@developerz/domain';
import { IconChevronDown } from '@developerz/ui';
import { createSignal, For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { ModeSaves } from '../lib/agent-mode-save';
import type { ModelSaves } from '../lib/agent-model-save';
import type { AgentModeView } from '../lib/agent-modes';
import type { AgentActivitySlice, CoderTaskTagPurpose } from '../lib/agents';
import {
  AGENT_NAME_KEY,
  type AgentStatus,
  agentStats,
  agentStatus,
  modeKeyForAgent,
} from '../lib/agents';
import type { AgentsPreviewEntryWire } from '../lib/agents-preview';
import type { RouteWire } from '../lib/llmRoutes';
import type { RepoRouteSaves } from '../lib/repo-route-save';
import { AgentAdvancedDrawer } from './AgentAdvancedDrawer';
import { AgentModeSwitch } from './AgentModeSwitch';
import { AgentQueueDrawer } from './AgentQueueDrawer';
import { AgentCardFrame } from './AgentsFrames';
import { ModelPicker, type ModelPickerProps } from './ModelPicker';
import { ModelSaveNotice } from './ModelSaveNotice';
import { StatusPill } from './StatusPill';
import { buttonClass } from './ui/Button';

// One agent's card (#5209, epic #5200). Presenter — every read is already resolved
// (the container/`AgentsView` owns them); this file paints ONE agent, plus its
// `ModelPicker` and the inline notice of a refused save (#5210), the mode switch
// on a mode-backed agent, the trigger for the Advanced drawer (#5211), and on an
// agent that holds tasks the "View queue" drawer (#6210). The route writes are
// `lib/agent-model-save.ts`'s; the mode writes are `lib/agent-mode-save.ts`'s.

/**
 * Each agent's one-line job: what the card says the agent DECIDES, total over
 * {@link AgentId}, so a roster addition is a compile error here until it has a
 * line. The Triage card states what the judge decides. No job line names the
 * model that picks the judge: the honesty line under it says so, and only while
 * the account pinned no `triage` route (`AgentsView`'s `honestyFor`).
 */
const JOB_KEY: Readonly<Record<AgentId, string>> = {
  coder: 'agents.job.coder',
  reviewer: 'agents.job.reviewer',
  ci_fixer: 'agents.job.ci_fixer',
  planner: 'agents.job.planner',
  scout: 'agents.job.scout',
  devops: 'agents.job.devops',
  gardener: 'agents.job.gardener',
  escalation: 'agents.job.escalation',
  triage: 'agents.job.triage',
  screen: 'agents.job.screen',
  default: 'agents.job.default',
};

export type AgentCardProps = {
  readonly agent: AgentId;
  /** The route purpose this card's picker pins (`AGENT_ROUTE_PURPOSE`). */
  readonly purpose: LlmRoutePurposeAny;
  /** That purpose's ORG route, or undefined when it has none. */
  readonly route: RouteWire | undefined;
  /** The ORG keys list the picker's options are built from. */
  readonly keys: ModelPickerProps['keys'];
  /** The caller may pin (`write:billing`, mirrored from the role). */
  readonly canEdit: boolean;
  readonly saves: ModelSaves;
  /** `provider/model` or a key label, or null for "Automatic" (`pinTriggerValue`). */
  readonly pinValue: string | null;
  readonly repoOverrides: number;
  /** `undefined`: no record (a null-shaped agent, or the read has none for it yet). */
  readonly activity: AgentActivitySlice | undefined;
  /** Only for the eight agents that name a preview purpose (`AGENT_PREVIEW_PURPOSE`'s
   * subset, not the pin-purpose map's subset). */
  readonly preview: AgentsPreviewEntryWire | undefined;
  /** True once the preview READ itself has failed — renders "Couldn't check right now"
   * instead of silently omitting the row (still-loading omits it instead). */
  readonly previewFailed: boolean;
  /** True once the activity READ itself has failed — the stats line says
   * "Couldn't check right now" rather than going blank (still-loading shows nothing). */
  readonly activityFailed: boolean;
  readonly showsPreview: boolean;
  /** Pre-translated honesty lines (`AgentsView` computes these — some need the
   * pin state or a template-override count this card does not otherwise hold). */
  readonly honesty: readonly string[];
  readonly changedBy: JSX.Element;

  // #5211: the mode switch + the Advanced drawer.
  /** This purpose's REPO-scope routes only, for the drawer's Per-repo section. */
  readonly repoRoutes: readonly RouteWire[];
  readonly repoNames: ReadonlyMap<string, string>;
  readonly repoSaves: RepoRouteSaves;
  /** Coder only: the org routes for the three task-tag purposes. */
  readonly taskTagRoutes: ReadonlyMap<string, RouteWire>;
  /** Coder only: each task-tag purpose's REPO-scope rows (#5436). */
  readonly taskTagRepoRoutes: ReadonlyMap<CoderTaskTagPurpose, readonly RouteWire[]>;
  /** The resolved ORG mode row this agent's switch/tools edit, or undefined for
   * an agent no mode governs OR a caller without `write:prompts` (the read
   * itself may have 403'd — `canEditModes` is what decides whether to show
   * "Ask an admin" instead of trying). */
  readonly modeView: AgentModeView | undefined;
  readonly canEditModes: boolean;
  readonly modeSaves: ModeSaves;
  readonly token: string | null;
  readonly onKeyRenamed: () => void;
};

/** One roster agent's card: its model, mode switch, Advanced drawer and, when it holds tasks, View queue (#6210). */
export function AgentCard(props: AgentCardProps): JSX.Element {
  const [drawerOpen, setDrawerOpen] = createSignal(false);
  const [queueOpen, setQueueOpen] = createSignal(false);
  // No button on an agent that runs no task of its own (screen, default):
  // an empty list there would read as "nothing to do" (#6210).
  /** Does this agent run tasks of its own, so a queue exists to show (`agentQueueScope`)? */
  const hasQueue = (): boolean => agentQueueScope(props.agent).kind !== 'none';
  const modeKey = (): AgentModeWritableKey<'org'> | undefined => modeKeyForAgent(props.agent);
  // ONE pill per card, always (#5624): every agent resolves to exactly one of
  // Off / Not in use / On demand / Stuck / Waiting / Running {n} / Idle.
  const status = (): AgentStatus =>
    agentStatus(
      props.agent,
      props.showsPreview ? props.preview : undefined,
      props.activity,
      props.pinValue,
    );
  const stats = () => agentStats(props.agent, props.activity, props.activityFailed);

  return (
    <AgentCardFrame
      data-agent={props.agent}
      head={
        <>
          <h2 class="card__title">{t(AGENT_NAME_KEY[props.agent])}</h2>
          <StatusPill tone={status().pill.tone}>{status().pill.label}</StatusPill>
        </>
      }
      job={
        <>
          <p class="card__desc">{t(JOB_KEY[props.agent])}</p>
          <Show when={props.honesty.length > 0}>
            <ul class="agent-card__honesty">
              {props.honesty.map((line) => (
                <li>{line}</li>
              ))}
            </ul>
          </Show>
        </>
      }
      model={
        <>
          <ModelPicker
            agentName={t(AGENT_NAME_KEY[props.agent])}
            purpose={props.purpose}
            route={props.route}
            pinValue={props.pinValue}
            previewPick={props.preview?.pick?.model}
            keys={props.keys}
            canEdit={props.canEdit && props.saves.refusal(props.purpose)?.refusal.kind !== 'locked'}
            busy={props.saves.isBusy(props.purpose)}
            onChoose={(change) => void props.saves.choose(props.purpose, change)}
          />
          <ModelSaveNotice
            pending={props.saves.refusal(props.purpose)}
            busy={props.saves.isBusy(props.purpose)}
            onApplyAnyway={() => void props.saves.applyAnyway(props.purpose)}
            onApplyReset={() => void props.saves.applyWithoutEffort(props.purpose)}
            onDismiss={() => props.saves.dismiss(props.purpose)}
          />
        </>
      }
      stats={
        <>
          <Show
            when={status().line}
            fallback={
              <Show when={props.showsPreview && props.preview === undefined && props.previewFailed}>
                <p class="agent-card__line agent-card__line--muted">
                  {t('agents.line.unavailable')}
                </p>
              </Show>
            }
          >
            {(line) => <p class="agent-card__line">{line()}</p>}
          </Show>

          <p class="agent-card__activity">
            <For
              each={(() => {
                const st = stats();
                return st.kind === 'segments' ? st.segments : [];
              })()}
            >
              {(seg, i) => (
                <>
                  {i() > 0 ? ' · ' : ''}
                  <span classList={{ 'agent-card__rate--amber': seg.amber }} title={seg.title}>
                    {seg.text}
                  </span>
                </>
              )}
            </For>
          </p>
        </>
      }
      foot={
        <>
          <Show when={modeKey()}>
            {(key) => (
              <AgentModeSwitch
                modeKey={key()}
                view={props.modeView}
                activityEnabled={props.activity?.mode_enabled}
                canEdit={props.canEditModes}
                busy={props.modeSaves.isBusy(key())}
                onConfirm={(enabled) => {
                  if (props.modeView === undefined) return;
                  void props.modeSaves.setEnabled(props.modeView, enabled);
                }}
                refusal={props.modeSaves.refusal(key())}
                onDismissRefusal={() => props.modeSaves.dismiss(key())}
              />
            )}
          </Show>

          <button
            type="button"
            class={buttonClass({ variant: 'ghost', size: 'sm' })}
            aria-label={t('agents.advanced_aria', { agent: t(AGENT_NAME_KEY[props.agent]) })}
            aria-haspopup="dialog"
            aria-expanded={drawerOpen()}
            onClick={() => setDrawerOpen(true)}
          >
            {t('agents.advanced')}
            <IconChevronDown
              class={`agent-card__chevron${drawerOpen() ? ' agent-card__chevron--open' : ''}`}
              size="0.875rem"
            />
          </button>

          <Show when={hasQueue()}>
            <button
              type="button"
              class={buttonClass({ variant: 'ghost', size: 'sm' })}
              aria-label={t('agents.queue.open_aria', { agent: t(AGENT_NAME_KEY[props.agent]) })}
              aria-haspopup="dialog"
              aria-expanded={queueOpen()}
              onClick={() => setQueueOpen(true)}
            >
              {t('agents.queue.open')}
            </button>
          </Show>

          {props.changedBy}

          <Show when={props.repoOverrides > 0}>
            <p class="agent-card__repo-chip">{t('agents.repo_chip', { n: props.repoOverrides })}</p>
          </Show>
        </>
      }
    >
      <AgentAdvancedDrawer
        open={drawerOpen()}
        onClose={() => setDrawerOpen(false)}
        agent={props.agent}
        agentName={t(AGENT_NAME_KEY[props.agent])}
        purpose={props.purpose}
        route={props.route}
        keys={props.keys}
        canEdit={props.canEdit}
        saves={props.saves}
        onKeyRenamed={props.onKeyRenamed}
        modeKey={modeKey()}
        modeView={props.modeView}
        canEditModes={props.canEditModes}
        modeSaves={props.modeSaves}
        repoRoutes={props.repoRoutes}
        repoNames={props.repoNames}
        repoSaves={props.repoSaves}
        token={props.token}
        taskTagRoutes={props.taskTagRoutes}
        taskTagRepoRoutes={props.taskTagRepoRoutes}
      />
      <Show when={hasQueue()}>
        <AgentQueueDrawer
          agent={props.agent}
          agentName={t(AGENT_NAME_KEY[props.agent])}
          open={queueOpen()}
          onClose={() => setQueueOpen(false)}
          token={props.token}
          handOverModel={props.pinValue}
        />
      </Show>
    </AgentCardFrame>
  );
}
